/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useRef } from 'react';

import { OverflowCarousel, getColorFromColorName, resolveIsDarkBackground } from '@coursera-web/cds-labs';

import Imgix from 'js/components/Imgix';
import { tupleToStringKey } from 'js/lib/stringKeyTuple';

import { Typography2, breakpoints } from '@coursera/cds-core';

import { EoiOptimizationsContextProvider } from 'bundles/expression-of-interest/components/EoiOptimizationsContext';
import type { Maybe } from 'bundles/premium-hub/__generated__/baseContentfulTypes';
import type { ExpressionOfInterestContainerFragment } from 'bundles/premium-hub/__generated__/contentfulTypes';
import { EoiWidgetContainer } from 'bundles/premium-hub/components/degrees/EoiWidgetContainer';
import HubHeroStickyActionBar from 'bundles/premium-hub/components/degrees/redesign/HubHeroStickyActionBar';
import Container from 'bundles/premium-hub/components/shared/Container';
import { HubPages } from 'bundles/premium-hub/constants';
import { ProductTypes } from 'bundles/university-program-qualification/utils/constants';

export type HubHeroLogo = {
  id: string;
  url: string;
  alt: string;
};

export type HubHeroValueProp = {
  title: string;
  description?: string;
};

export type HubHeroModuleData = {
  heading: string;
  subHeading?: string;
  logos?: HubHeroLogo[];
  valueProps?: HubHeroValueProp[];
  // Named color from Contentful; takes priority over `customBackgroundColor` (a HEX or gradient value).
  backgroundColor?: Maybe<string>;
  customBackgroundColor?: Maybe<string>;
};

export type HubHeroModuleProps = HubHeroModuleData & {
  slug?: Maybe<string>;
  categoryName?: string;
  currentHubPage: (typeof HubPages)[keyof typeof HubPages];
  productId?: string;
  surveyProductType?: string;
  expressionOfInterestContainer?: Maybe<ExpressionOfInterestContainerFragment>;
};

const LOGO_HEIGHT = 64;

const styles = {
  background: (background: string) => css`
    padding: calc(var(--cds-spacing-100) * 5) 0;
    background: ${background};
  `,
  layout: css`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: var(--cds-spacing-300);

    ${breakpoints.up('md')} {
      grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
      gap: var(--cds-spacing-800);
    }
  `,
  content: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-400);
    min-width: 0;
  `,
  headingGroup: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-150);
  `,
  subheading: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
  subheadingOnDark: css`
    color: var(--cds-color-neutral-primary-invert);
  `,
  logoList: css`
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: max-content;
    align-items: center;
    gap: var(--cds-spacing-400);
  `,
  logo: css`
    display: block;
    width: auto;
    height: ${LOGO_HEIGHT}px;
    object-fit: contain;
    mix-blend-mode: multiply;
  `,
  logoOnDark: css`
    box-sizing: border-box;
    border-radius: var(--cds-border-radius-100);
    mix-blend-mode: normal;

    @supports (background-color: light-dark(var(--cds-color-white-0), #fff)) {
      /* Logos are designed for light backgrounds, so on dark ones they sit on a white chip. */
      /* stylelint-disable-next-line color-no-hex, function-no-unknown */
      background-color: light-dark(var(--cds-color-white-0), #fff);
    }
  `,
  valueProps: css`
    display: none;
    margin: 0;
    padding: var(--cds-spacing-200);
    list-style: none;
    background-color: var(--cds-color-neutral-background-primary);
    border-radius: var(--cds-border-radius-100);
    box-shadow: 0 0 5px 0 var(--cds-color-darken-100);

    ${breakpoints.up('md')} {
      display: flex;
    }
  `,
  valueProp: css`
    flex: 1 1 0;
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
    min-width: 0;
    padding: 0 var(--cds-spacing-200);

    &:not(:first-of-type) {
      border-left: 1px solid var(--cds-color-grey-100);
    }
  `,
  valuePropDescription: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
};

const HubHeroModule = ({
  heading,
  subHeading,
  logos = [],
  valueProps = [],
  backgroundColor,
  customBackgroundColor,
  slug,
  categoryName,
  currentHubPage,
  productId,
  surveyProductType,
  expressionOfInterestContainer,
}: HubHeroModuleProps) => {
  const heroRef = useRef<HTMLDivElement>(null);
  const completeProductId = tupleToStringKey([surveyProductType || '', productId || '']);
  const eoiProductType =
    currentHubPage === HubPages.MainHubPage ? ProductTypes.DEGREE_HUB : ProductTypes.DEGREE_CATEGORY;

  const namedColor = getColorFromColorName(backgroundColor);
  const customColor = namedColor ? null : getColorFromColorName(customBackgroundColor?.replace(/;$/, ''));
  const isDarkBackground = resolveIsDarkBackground({
    backgroundColor: namedColor ? backgroundColor : null,
    customBackgroundColor: customColor,
  });
  const textColor = isDarkBackground ? 'invertBody' : 'body';

  return (
    <div
      css={styles.background(namedColor ?? customColor ?? 'var(--cds-color-neutral-background-primary)')}
      data-testid="hub-hero-background"
    >
      <Container>
        <div css={styles.layout} ref={heroRef} data-testid="hub-hero-module">
          <div css={styles.content}>
            <div css={styles.headingGroup}>
              <Typography2 variant="titleLarge" component="h1" color={textColor}>
                {heading}
              </Typography2>
              {subHeading && (
                <Typography2
                  variant="subtitleLarge"
                  component="p"
                  css={[styles.subheading, isDarkBackground && styles.subheadingOnDark]}
                >
                  {subHeading}
                </Typography2>
              )}
            </div>

            {logos.length > 0 && (
              <OverflowCarousel css={styles.logoList}>
                {logos.map(({ id, url, alt }) => (
                  <Imgix
                    key={id}
                    src={url}
                    alt={alt}
                    css={[styles.logo, (!customColor || isDarkBackground) && styles.logoOnDark]}
                    imgParams={{ auto: 'format,compress', fit: 'max', height: LOGO_HEIGHT * 2 }}
                  />
                ))}
              </OverflowCarousel>
            )}

            {valueProps.length > 0 && (
              <ul css={styles.valueProps}>
                {valueProps.map(({ title, description }) => (
                  <li key={title} css={styles.valueProp} data-testid="hub-hero-value-prop">
                    <Typography2 variant="subtitleMedium" component="p">
                      {title}
                    </Typography2>
                    {description && (
                      <Typography2 variant="bodySecondary" component="p" css={styles.valuePropDescription}>
                        {description}
                      </Typography2>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {productId && slug && (
            <div data-testid="hub-hero-eoi-container">
              <EoiOptimizationsContextProvider slug={slug} productType={eoiProductType}>
                <EoiWidgetContainer
                  productName={categoryName || heading}
                  productId={completeProductId}
                  programInterestText={expressionOfInterestContainer?.programInterestText}
                  eoiButtonText={expressionOfInterestContainer?.emailMeButtonText}
                />
              </EoiOptimizationsContextProvider>
            </div>
          )}
        </div>
        <HubHeroStickyActionBar anchorRef={heroRef} programName={categoryName || heading} />
      </Container>
    </div>
  );
};

export default HubHeroModule;
